import axios from "axios";
import { useEffect, useState, useCallback } from "react";
import { Spinner, Form, Pagination, Table } from "react-bootstrap";
import Layout from "../../../components/Layouts/Layouts/Layout";
import DashboardHeader from "../../../components/basic/DashboardHeader";
import { PaymentConfirmModal } from "../../../components/basic/fileViewModal";
import { getURLbyEndPoint } from "../../../store/api";

const Payment = () => {
  const [paymentDetails, setPaymentDetails] = useState([]);
  const [accountStatus, setAccountStatus] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const itemsPerPage = 10;

  // Fetch payment details with pagination
  const fetchPaymentDetails = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const response = await axios.get(getURLbyEndPoint("getAddFundRequests"), {
        params: { page, limit: itemsPerPage, searchTerm, accountStatus },
      });

      if (response.status === 200) {
        setPaymentDetails(response.data.data);
        setTotalPages(Math.ceil(response.data.totalCount / itemsPerPage));
      } else {
        console.error("Failed to fetch payment details.");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, accountStatus]);

  // Handle input changes for caseId, agreementId, printId
  const handleInputChange = (paymentId, field, value) => {
    setFormData((prev) => {
      const updatedFormData = {
        ...prev,
        [paymentId]: {
          ...prev[paymentId] || { caseId: "", agreementId: "", printId: "" },
          [field]: value,
        },
      };
      return updatedFormData;
    });
  };

  const validateFields = (paymentId) => {
    const data = formData[paymentId] || { caseId: "", agreementId: "", printId: "" };
    return data.caseId?.trim() && data.agreementId?.trim() && data.printId?.trim();
  };

  const handleUpdateRejectAddFund = async (clientId, userId, status, appNo, paymentId) => {
    if (status && appNo?.startsWith("AECCI/BD/")) {
      if (!validateFields(paymentId)) {
        alert("Case ID, Agreement ID, and Print ID are required for AECCI/BD/ payments.");
        return;
      }
    }

    setIsLoading(true);
    try {
      const payload = { clientId, userId, status, appNo };

      if (status && appNo?.startsWith("AECCI/BD/")) {
        const { caseId, agreementId, printId } = formData[paymentId] || {};
        payload.caseId = caseId;
        payload.agreementId = agreementId;
        payload.printId = printId;
      }

      const response = await axios.put(getURLbyEndPoint("acceptRejectAddFund"), payload);
      alert(response.data.message);
      window.location.reload();
    } catch (error) {
      console.error("Error:", error.response?.data || error.message);
      alert("Failed to process the request.");
    } finally {
      setIsLoading(false);
    }
  };


  const filteredData = paymentDetails.filter((item) => {
    if (accountStatus && item.status !== accountStatus) return false;
    const companyName = item.companyName?.toLowerCase() || "";
    const remark = item.remark?.toLowerCase() || "";
    return (
      !searchTerm ||
      companyName.includes(searchTerm.toLowerCase()) ||
      remark.includes(searchTerm.toLowerCase())
    );
  });

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    fetchPaymentDetails(pageNumber);
  };

  const paginationItems = () => {
    const maxPaginationItems = 20;
    const startPage = Math.max(1, currentPage - Math.floor(maxPaginationItems / 2));
    const endPage = Math.min(totalPages, startPage + maxPaginationItems - 1);
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map((i) => (
      <Pagination.Item
        key={i}
        active={i === currentPage}
        onClick={() => handlePageChange(i)}
      >
        {i}
      </Pagination.Item>
    ));
  };

  useEffect(() => {
    setCurrentPage(1);
    fetchPaymentDetails(1);
  }, [searchTerm, accountStatus, fetchPaymentDetails]);

  return (
    <Layout>
      <div className="d-flex flex-column justify-content-center align-items-center px-3">
        <div className="main-width d-flex flex-column">
          <hr />
          <DashboardHeader heading="Services/ Payments" backUrl="/master-services" />
          <hr />
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex gap-3">
              {["", "Processing", "Approved", "Rejected"].map((status) => (
                <Form.Check
                  key={status}
                  inline
                  label={status || "All"}
                  name="group1"
                  type="radio"
                  checked={accountStatus === status}
                  onChange={() => setAccountStatus(status)}
                />
              ))}
            </div>
            <input
              className="form-control w-25"
              type="search"
              placeholder="Search Company Name/Remark"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          {loading ? (
            <div className="d-flex justify-content-center mt-3">
              <Spinner animation="border" />
            </div>
          ) : (
            <Table responsive striped bordered hover className="text-center">
              <thead>
                <tr>
                  <th className="align-middle">SR NO.</th>
                  <th className="align-middle">User</th>
                  <th className="align-middle">Bank Details</th>
                  <th className="align-middle">Balance</th>
                  <th className="align-middle">Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length > 0 ? (
                  filteredData.map((data, index) => (
                    <tr key={data._id}>
                      <td>
                        {index + 1 + (currentPage - 1) * itemsPerPage}
                        <div className="max-content">
                          Created at:
                          <br />
                          <span className="text-success">
                            {new Intl.DateTimeFormat("en-GB", {
                              year: "numeric",
                              month: "numeric",
                              day: "numeric",
                              hour: "numeric",
                              minute: "numeric",
                            }).format(new Date(data.createdAt))}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div className="text-start max-content">
                          <h6>
                            Company Name: <span className="text-secondary">{data.companyName}</span>
                          </h6>
                          <h6>
                            Membership No.: <span className="text-secondary">{data.memberShipNo}</span>
                          </h6>
                        </div>
                      </td>
                      <td>
                        <div className="text-start max-content">
                          <h6>
                            Bank Name: <span className="text-secondary">{data.registeredBank}</span>
                          </h6>
                          <h6>
                            ACC. No.: <span className="text-secondary">{data.registeredAccountNo}</span>
                          </h6>
                          <h6>
                            IFSC Code: <span className="text-secondary">{data.IFSCCode}</span>
                          </h6>
                        </div>
                      </td>
                      <td>
                        <div className="text-start max-content">
                          <h6>
                            Balance: <span className="text-secondary">{data.balance}</span>
                          </h6>
                          <h6>
                            Amount: <span className="text-secondary">{data.amount}</span>
                          </h6>
                          <h6>
                            Type: <span className="text-secondary">{data.paymentType}</span>
                          </h6>
                          {data.utrNo && (
                            <h6>
                              Utr No.: <span className="text-secondary">{data.utrNo}</span>
                            </h6>
                          )}
                          {data.appNo && (
                            <h6>
                              App No.: <span className="text-secondary">{data.appNo}</span>
                            </h6>
                          )}
                          {data.paymentSource && (
                            <h6>
                              Payment Source:
                              <span className="text-secondary">
                                {data.paymentSource.split("\n").map((line, index) => (
                                  <div key={index}>{line}</div>
                                ))}
                              </span>
                            </h6>
                          )}
                          {data.paymentScreenShot && (
                            <a href={data.paymentScreenShot} target="_blank" rel="noopener noreferrer">
                              Payment ScreenShot
                            </a>
                          )}
                        </div>
                      </td>
                      <td>
                        <h6 className={data.status === "Rejected" ? "text-danger" : "text-success"}>
                          {data.status}
                        </h6>
                        {data.status === "Processing" && data.appNo?.startsWith("AECCI/BD/") && (
                          <div className="mb-2">
                            <Form.Group className="mb-2">
                              <Form.Control
                                type="text"
                                placeholder="Enter Case ID"
                                value={formData[data._id]?.caseId || ""}
                                onChange={(e) => handleInputChange(data._id, "caseId", e.target.value)}
                              />
                            </Form.Group>
                            <Form.Group className="mb-2">
                              <Form.Control
                                type="text"
                                placeholder="Enter Agreement ID"
                                value={formData[data._id]?.agreementId || ""}
                                onChange={(e) => handleInputChange(data._id, "agreementId", e.target.value)}
                              />
                            </Form.Group>
                            <Form.Group className="mb-2">
                              <Form.Control
                                type="text"
                                placeholder="Enter Print ID"
                                value={formData[data._id]?.printId || ""}
                                onChange={(e) => handleInputChange(data._id, "printId", e.target.value)}
                              />
                            </Form.Group>
                          </div>
                        )}
                        {data.status === "Processing" ? (
                          <PaymentConfirmModal
                            handleUpdateRejectAddFund={handleUpdateRejectAddFund}
                            disabled={isLoading}
                            clientId={data.clientId}
                            userId={data._id}
                            paymentId={data._id}
                            appNo={data.appNo}
                          />
                        ) : (
                          <div className="fw-bold text-center">
                            <span className={data.status === "Rejected" ? "text-danger" : "text-success"}>
                              {new Intl.DateTimeFormat("en-GB", {
                                year: "numeric",
                                month: "numeric",
                                day: "numeric",
                                hour: "numeric",
                                minute: "numeric",
                              }).format(new Date(data.updatedAt))}
                            </span>
                          </div>
                        )}
                        {data.remark && <span>{data.remark}</span>}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="text-center">
                      No Matching Data
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
          {totalPages > 1 && (
            <div className="d-flex justify-content-start mt-2">
              <Pagination>
                <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                {paginationItems()}
                <Pagination.Next
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                />
                <Pagination.Last onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} />
              </Pagination>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Payment;